// ==================== Finding Fingerprint ====================

export function generateFindingFingerprint(tenantId, ruleId, affectedObjectType, affectedObjectId, materialEvidenceKey) {
  var input = tenantId + '|' + ruleId + '|' + affectedObjectType + '|' + affectedObjectId + '|' + materialEvidenceKey;
  return simpleHash(input);
}

function simpleHash(str) {
  var hash = 0;
  for (var i = 0; i < str.length; i++) {
    var char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return 'fp-' + Math.abs(hash).toString(36);
}

// ==================== Severity & Confidence ====================

export var SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical', color: '#dc2626' },
  { value: 'high', label: 'High', color: '#ea580c' },
  { value: 'medium', label: 'Medium', color: '#ca8a04' },
  { value: 'low', label: 'Low', color: '#2563eb' },
  { value: 'informational', label: 'Informational', color: '#6b7280' }
];

export var CONFIDENCE_OPTIONS = [
  { value: 'very-high', label: 'Very High' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }
];

export function getSeverityInfo(severity) {
  var found = SEVERITY_OPTIONS.find(function(s) { return s.value === severity; });
  return found || { value: 'informational', label: 'Informational', color: '#6b7280' };
}

export function getConfidenceLabel(confidence) {
  var found = CONFIDENCE_OPTIONS.find(function(c) { return c.value === confidence; });
  return found ? found.label : confidence;
}

// ==================== Finding Lifecycle ====================

export var FINDING_STATUSES = [
  'open',
  'triaged',
  'assigned',
  'planned',
  'in_progress',
  'awaiting_customer',
  'resolved',
  'accepted_risk',
  'excluded',
  'dismissed',
  'reopened'
];

export var FINDING_CATEGORIES = [
  { key: 'licensing', label: 'Licensing' },
  { key: 'users', label: 'Users' },
  { key: 'authentication', label: 'Authentication' },
  { key: 'privileged-access', label: 'Privileged Access' },
  { key: 'guests', label: 'Guests' },
  { key: 'groups', label: 'Groups' },
  { key: 'applications', label: 'Applications' },
  { key: 'conditional-access', label: 'Conditional Access' },
  { key: 'microsoft-recommendations', label: 'Microsoft Recommendations' },
  { key: 'collection-health', label: 'Collection Health' },
  { key: 'data-quality', label: 'Data Quality' }
];

// ==================== Licence Confidence Scoring ====================

export function calculateLicenceConfidenceScore(signals) {
  var score = 0;

  if (signals.accountDisabled) score += 50;
  if (signals.noSuccessfulSignInFor90Days) score += 20;
  if (signals.noM365WorkloadActivityFor90Days) score += 30;
  if (signals.neverSignedInAndAccountOlderThan30Days) score += 25;
  if (signals.directAssignment) score += 5;

  if (signals.isServiceAccount) score -= 60;
  if (signals.isBreakGlass) score -= 100;
  if (signals.approvedLeaveException) score -= 100;
  if (signals.usageIdentityConcealed) score -= 25;
  if (signals.recentTeamsOrExchangeActivity) score -= 60;
  if (signals.isPrivileged) score -= 20;

  var confidence;
  if (score >= 80) confidence = 'very-high';
  else if (score >= 60) confidence = 'high';
  else if (score >= 35) confidence = 'medium';
  else confidence = 'low';

  return { score: score, confidence: confidence };
}

// ==================== Cost Calculation ====================

export function normaliseMonthlyCost(unitPrice, billingFrequency, totalTermPrice, termMonths) {
  if (billingFrequency === 'monthly') {
    return unitPrice;
  }
  if (billingFrequency === 'annual') {
    return unitPrice / 12;
  }
  if (billingFrequency === 'term' && totalTermPrice && termMonths) {
    return totalTermPrice / termMonths;
  }
  return null;
}

export function calculateEstimatedMonthlySaving(monthlyUnitCost, removableSeatCount) {
  if (monthlyUnitCost === null || monthlyUnitCost === undefined || removableSeatCount === null || removableSeatCount === undefined) {
    return null;
  }
  return monthlyUnitCost * removableSeatCount;
}

export function calculateEstimatedAnnualSaving(estimatedMonthlySaving) {
  if (estimatedMonthlySaving === null || estimatedMonthlySaving === undefined) {
    return null;
  }
  return estimatedMonthlySaving * 12;
}

// ==================== Licence Rules ====================

export var LICENCE_RULES = [
  {
    ruleId: 'LIC-001',
    name: 'Disabled user with paid licence',
    defaultSeverity: 'high',
    defaultConfidence: 'very-high',
    category: 'licensing'
  },
  {
    ruleId: 'LIC-002',
    name: 'Deleted user retaining licence',
    defaultSeverity: 'high',
    defaultConfidence: 'very-high',
    category: 'licensing'
  },
  {
    ruleId: 'LIC-003',
    name: 'Inactive licensed user',
    defaultSeverity: 'high',
    defaultConfidence: 'high',
    category: 'licensing',
    defaultInactivityDays: 90
  },
  {
    ruleId: 'LIC-004',
    name: 'Never-used paid licence',
    defaultSeverity: 'high',
    defaultConfidence: 'high',
    category: 'licensing',
    defaultGraceDays: 30
  },
  {
    ruleId: 'LIC-005',
    name: 'Potentially inactive',
    defaultSeverity: 'medium',
    defaultConfidence: 'medium',
    category: 'licensing'
  },
  {
    ruleId: 'LIC-006',
    name: 'Unused add-on',
    defaultSeverity: 'medium',
    defaultConfidence: 'medium',
    category: 'licensing'
  },
  {
    ruleId: 'LIC-007',
    name: 'Duplicate service plan coverage',
    defaultSeverity: 'medium',
    defaultConfidence: 'high',
    category: 'licensing'
  },
  {
    ruleId: 'LIC-008',
    name: 'Direct assignment drift',
    defaultSeverity: 'low',
    defaultConfidence: 'very-high',
    category: 'licensing'
  },
  {
    ruleId: 'LIC-009',
    name: 'Licence assignment error',
    defaultSeverity: 'high',
    defaultConfidence: 'very-high',
    category: 'licensing'
  },
  {
    ruleId: 'LIC-010',
    name: 'Excess licence inventory',
    defaultSeverity: 'medium',
    defaultConfidence: 'medium',
    category: 'licensing'
  },
  {
    ruleId: 'LIC-011',
    name: 'Capacity exhaustion risk',
    defaultSeverity: 'high',
    defaultConfidence: 'medium',
    category: 'licensing'
  },
  {
    ruleId: 'LIC-012',
    name: 'Possible downgrade candidate',
    defaultSeverity: 'medium',
    defaultConfidence: 'medium',
    category: 'licensing'
  }
];

// ==================== Health Score ====================

export var HEALTH_SCORE_DOMAINS = [
  { key: 'authentication', label: 'Authentication Posture', defaultWeight: 25 },
  { key: 'privileged-access', label: 'Privileged Access Hygiene', defaultWeight: 20 },
  { key: 'user-lifecycle', label: 'User Lifecycle Hygiene', defaultWeight: 15 },
  { key: 'guest-governance', label: 'Guest Governance', defaultWeight: 10 },
  { key: 'application', label: 'Application Hygiene', defaultWeight: 15 },
  { key: 'group-governance', label: 'Group Governance', defaultWeight: 5 },
  { key: 'microsoft-recommendations', label: 'Microsoft Recommendations', defaultWeight: 10 }
];

export function calculateDomainScore(controls) {
  if (!controls || controls.length === 0) return null;

  var totalWeight = 0;
  var weightedScore = 0;
  var availableControls = 0;

  controls.forEach(function(control) {
    if (control.state !== 'unavailable') {
      availableControls++;
      totalWeight += control.weight;
      var coverage = control.compliantObjects / Math.max(control.eligibleObjects, 1);
      weightedScore += coverage * control.weight * 100;
    }
  });

  if (totalWeight === 0) return null;

  return {
    score: weightedScore / totalWeight,
    dataCompleteness: availableControls / controls.length * 100
  };
}

export function calculateHealthScore(domainResults) {
  var activeDomains = [];
  var totalWeight = 0;
  var weightedScore = 0;

  domainResults.forEach(function(domain) {
    if (domain.result !== null) {
      activeDomains.push(domain);
      totalWeight += domain.weight || 0;
      weightedScore += domain.result.score * (domain.weight || 0);
    }
  });

  if (totalWeight === 0 || activeDomains.length === 0) return null;

  return {
    score: Math.round(weightedScore / totalWeight),
    activeDomains: activeDomains.length,
    totalDomains: domainResults.length,
    dataCompleteness: activeDomains.length / domainResults.length * 100
  };
}

// ==================== User Classification ====================

export var USER_CLASSIFICATIONS = [
  { value: 'human-user', label: 'Human User' },
  { value: 'guest-user', label: 'Guest User' },
  { value: 'shared-account', label: 'Shared Account' },
  { value: 'service-account', label: 'Service Account' },
  { value: 'break-glass', label: 'Break Glass' },
  { value: 'room-resource', label: 'Room Resource' },
  { value: 'equipment-resource', label: 'Equipment Resource' },
  { value: 'unknown', label: 'Unknown' }
];

// ==================== Colour & Accessibility Helpers ====================

export function getSeverityColor(severity) {
  var map = {
    critical: '#dc2626',
    high: '#ea580c',
    medium: '#ca8a04',
    low: '#2563eb',
    informational: '#6b7280'
  };
  return map[severity] || '#6b7280';
}

export function getSeverityBgColor(severity) {
  var map = {
    critical: '#fef2f2',
    high: '#fff7ed',
    medium: '#fefce8',
    low: '#eff6ff',
    informational: '#f9fafb'
  };
  return map[severity] || '#f9fafb';
}

export function getTenantStatusColor(status) {
  var map = {
    healthy: '#16a34a',
    warning: '#ca8a04',
    failed: '#dc2626',
    'consent-required': '#6b7280'
  };
  return map[status] || '#6b7280';
}

export function getDataFreshnessColor(state) {
  var map = {
    fresh: '#16a34a',
    delayed: '#ca8a04',
    stale: '#dc2626',
    failed: '#dc2626',
    'never-collected': '#6b7280',
    partial: '#ca8a04'
  };
  return map[state] || '#6b7280';
}

export function getDataFreshnessLabel(state) {
  var map = {
    fresh: 'Fresh',
    delayed: 'Delayed',
    stale: 'Stale',
    failed: 'Failed',
    'never-collected': 'Never Collected',
    partial: 'Partial'
  };
  return map[state] || state;
}

export function getCapabilityStateLabel(state) {
  var map = {
    available: 'Available',
    'missing-permission': 'Missing Permission',
    'missing-licence': 'Missing Licence',
    'unsupported-cloud': 'Unsupported Cloud',
    'consent-expired': 'Consent Expired',
    'access-denied': 'Access Denied',
    'collection-failed': 'Collection Failed',
    'not-configured': 'Not Configured',
    unknown: 'Unknown'
  };
  return map[state] || state;
}

export function getCollectionStatusLabel(status) {
  var map = {
    healthy: 'Healthy',
    warning: 'Warning',
    failed: 'Collection Failed',
    'consent-required': 'Consent Required'
  };
  return map[status] || status;
}

export function getJobStateLabel(state) {
  var map = {
    queued: 'Queued',
    leased: 'Leased',
    running: 'Running',
    succeeded: 'Succeeded',
    partially_succeeded: 'Partially Succeeded',
    failed: 'Failed',
    cancelled: 'Cancelled',
    retry_scheduled: 'Retry Scheduled',
    dead_lettered: 'Dead Lettered'
  };
  return map[state] || state;
}

// ==================== Formatting ====================

export function formatCurrency(amount, currency) {
  if (amount === null || amount === undefined) return 'Price unavailable';
  if (currency === undefined) currency = 'GBP';
  var symbol = currency === 'GBP' ? '\u00A3' : (currency === 'USD' ? '$' : (currency === 'CAD' ? 'C$' : currency));
  return symbol + Number(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatCompactCurrency(amount, currency) {
  if (amount === null || amount === undefined) return '--';
  if (currency === undefined) currency = 'GBP';
  var symbol = currency === 'GBP' ? '\u00A3' : (currency === 'USD' ? '$' : (currency === 'CAD' ? 'C$' : currency));
  var abs = Math.abs(Number(amount));
  if (abs >= 1e9) return symbol + (Number(amount) / 1e9).toFixed(1) + 'B';
  if (abs >= 1e6) return symbol + (Number(amount) / 1e6).toFixed(1) + 'M';
  if (abs >= 1e3) return symbol + (Number(amount) / 1e3).toFixed(1) + 'k';
  return symbol + Number(amount).toFixed(0);
}

export var REGION_COLORS = {
  GB: '#3b82f6',
  US: '#10b981',
  HK: '#f59e0b',
  CA: '#8b5cf6',
  LA: '#ec4899',
  ME: '#64748b'
};

export function getRegionColor(regionCode) {
  return REGION_COLORS[regionCode] || '#94a3b8';
}

export function formatPercentage(value, decimals) {
  if (value === null || value === undefined) return '--';
  if (decimals === undefined) decimals = 1;
  return Number(value).toFixed(decimals) + '%';
}

export function formatNumberWithCommas(value) {
  if (value === null || value === undefined) return '--';
  return Number(value).toLocaleString('en-GB');
}

export function formatCount(value) {
  if (value === null || value === undefined) return '--';
  return Number(value).toLocaleString('en-GB');
}

// ==================== Group Findings ====================

export var GROUP_FINDING_TYPES = [
  { key: 'no-owner', label: 'No Owner', severity: 'medium' },
  { key: 'single-owner', label: 'Only One Owner', severity: 'low' },
  { key: 'no-members', label: 'No Members', severity: 'low' },
  { key: 'guest-members', label: 'Guest Members Present', severity: 'low' },
  { key: 'more-guests-than-members', label: 'More Guests Than Members', severity: 'medium' },
  { key: 'privileged-role-assigned', label: 'Privileged Role Assigned', severity: 'medium' },
  { key: 'owner-is-guest', label: 'Owner is Guest', severity: 'high' },
  { key: 'owner-is-disabled', label: 'Owner is Disabled', severity: 'high' },
  { key: 'public-group', label: 'Public Microsoft 365 Group', severity: 'low' }
];

// ==================== Application Findings ====================

export var APPLICATION_FINDING_TYPES = [
  { key: 'no-owner', label: 'No Owner', severity: 'high' },
  { key: 'single-owner', label: 'Only One Owner', severity: 'medium' },
  { key: 'owner-disabled', label: 'Owner Disabled', severity: 'high' },
  { key: 'owner-is-guest', label: 'Owner is Guest', severity: 'high' },
  { key: 'secret-expiring-30', label: 'Secret Expiring within 30 Days', severity: 'high' },
  { key: 'secret-expiring-90', label: 'Secret Expiring within 90 Days', severity: 'medium' },
  { key: 'expired-secret', label: 'Expired Secret Retained', severity: 'critical' },
  { key: 'certificate-expiring', label: 'Certificate Expiring', severity: 'high' },
  { key: 'expired-certificate', label: 'Expired Certificate Retained', severity: 'critical' },
  { key: 'disabled-service-principal', label: 'Disabled Service Principal', severity: 'low' }
];

// ==================== Collection Job Types ====================

export var COLLECTION_JOB_TYPES = [
  { key: 'tenant-capability-check', label: 'Tenant Capability Check' },
  { key: 'tenant-full-sync', label: 'Tenant Full Sync' },
  { key: 'users-delta-sync', label: 'Users Delta Sync' },
  { key: 'users-signin-sync', label: 'Sign-in Sync' },
  { key: 'sku-sync', label: 'SKU Sync' },
  { key: 'licence-assignment-reconciliation', label: 'Licence Assignment Reconciliation' },
  { key: 'm365-usage-report-sync', label: 'M365 Usage Report Sync' },
  { key: 'authentication-registration-sync', label: 'Authentication Registration Sync' },
  { key: 'groups-delta-sync', label: 'Groups Delta Sync' },
  { key: 'group-membership-sync', label: 'Group Membership Sync' },
  { key: 'role-sync', label: 'Role Sync' },
  { key: 'application-sync', label: 'Application Sync' },
  { key: 'findings-evaluation', label: 'Findings Evaluation' },
  { key: 'metrics-snapshot', label: 'Metrics Snapshot' },
  { key: 'report-generation', label: 'Report Generation' }
];
